// @flow
// git history: see file ContactModal for version history
import React, { Fragment } from 'react';
import { Container, Row, Col } from 'Components/Grid';
import DealerModel from 'Stores/DealerModel';
import styles from './styles.scss';
import { withRouter } from 'react-router-dom';
import DeactivatedServiceMessage from './DeactivatedServiceMessage';
import CarsLink from './CarsLink';
import { CONTACT_TYPES, MODAL_TYPES } from './contactFormConstants';
import type { ContactFormState } from './contactFormConstants';
import ConsultationAppointment from './ConsultationAppointment';
import { hasCookie, getCookie } from 'Utils';
import { getAdjustedModalType } from 'Utils/getModalType';
import { getStateConfig } from './stateConfig';
import GlobalContext from '../../context/Global/index';
import { LeadForm } from './LeadForm';
import { formTypes } from 'Containers/ContactForm/LeadForm/formTypes';
import { UsedCarEvaluation } from 'Components/Header/LeadNav/UsedCarEvaluation';

type Props = {
  dealer: DealerModel,
  type: number,
  location: Object,
  updateOneUrlQuery?: () => void,
  updateMultipleUrlQuery?: () => void,
  setCloseTracking?: () => void,
  storybook?: {
    firstName?: string,
    lastName?: string,
    email?: string,
    telephone?: string,
    comment?: string,
    emailOptIn?: boolean,
    telephoneOptIn?: boolean,
    appointmentDate?: string,
    preferredTime?: string,
    activeStep?: number,
    completed?: boolean,
    transferError?: boolean,
    clientType?: string,
    preferredPayment?: string,
  },
};

class ContactForm extends React.Component<Props, ContactFormState> {
  constructor(props) {
    super(props);
    this.state = {
      validModalTypeFromUrl: Object.keys(MODAL_TYPES).includes(this.props.sharedState.modalType)
        ? this.props.sharedState.modalType
        : undefined,
      hwsconfig: window.hwsconfig || {},
      consultationAppointmentTab:
        this.props.sharedState.modalType === MODAL_TYPES.REQUEST_FOR_CONSULTATION_FK.name ? 0 : 1,
    };
  }

  componentDidMount() {
    const { sharedState, setSharedState } = this.props;

    // if we open an URL with 'showmodal=true' and 'campaignCode=testcode', campaignCode is not in sharedState and later not in payload
    const campaignCookie = hasCookie('campaignCode') ? getCookie('campaignCode') : false;

    if (campaignCookie && campaignCookie !== sharedState.campaignCode) {
      setSharedState('campaignCode', campaignCookie);
    }
  }

  get modalTypeName() {
    const modalTypeName =
      this.state.validModalTypeFromUrl ||
      Object.values(MODAL_TYPES).find(
        (formType) => formType.contactType === CONTACT_TYPES[this.props.type]
      ).name;

    const { isOsbUser, isSalesPartner, isServicePartner } = this.props.dealer.settings;

    const adjustedModalType = getAdjustedModalType({
      isServicePartner,
      isSalesPartner,
      isOsbUser,
      initialModalType: modalTypeName,
    });

    return adjustedModalType;
  }

  updateTabIndex = (index, modalType) => {
    this.setState({ consultationAppointmentTab: index, validModalTypeFromUrl: modalType });
  };

  render() {
    const {
      model,
      dealer,
      onAbort,
      sharedState,
      setSharedState,
      setCloseTracking,
      storybook,
      urlParams,
      updateOneUrlQuery,
      updateMultipleUrlQuery,
      flyout,
      leadtracking,
    } = this.props;

    const { hwsconfig } = this.state;
    const {
      enableFormRequestForProposal = true,
      enableFormOnlineServiceBooking = true,
      enableFormOnlineServiceRequest = true,
      enableFormTestDrive = true,
      enableFormRequestAConsultation = true,
      enableFormCarConfigurator = true,
    } = hwsconfig;

    // isOsbUser: TODO BE
    const { isOsbUser, isSalesPartner, isServicePartner } = dealer.settings;

    const commonModalProps = {
      defaultModel: model,
      dealer: dealer,
      onAbort: onAbort,
      onClose: onAbort,
      sharedState: sharedState,
      setSharedState: setSharedState,
      updateOneUrlQuery: updateOneUrlQuery,
      updateMultipleUrlQuery: updateMultipleUrlQuery,
    };

    const contents = [
      {
        modalType: MODAL_TYPES.REQUEST_FOR_PROPOSAL,
        isActive: enableFormRequestForProposal,
        renderContent: (context, updateGlobalContext) => (
          <LeadForm
            {...commonModalProps}
            storybook={storybook}
            modalType={MODAL_TYPES.REQUEST_FOR_PROPOSAL}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
            setCloseTracking={setCloseTracking}
            urlParams={urlParams}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.TEST_DRIVE,
        isActive: enableFormTestDrive,
        renderContent: (context, updateGlobalContext) => (
          <LeadForm
            {...commonModalProps}
            storybook={storybook}
            modalType={MODAL_TYPES.TEST_DRIVE}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
            setCloseTracking={setCloseTracking}
            urlParams={urlParams}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.NEWSLETTER,
        isActive: true,
        renderContent: (context, updateGlobalContext) => (
          <LeadForm
            {...commonModalProps}
            storybook={storybook}
            modalType={MODAL_TYPES.NEWSLETTER}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
            setCloseTracking={setCloseTracking}
            urlParams={urlParams}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.TOPCARS,
        isActive: true,
        label: this.props.dealer.settings.customTopCars.title,
        renderContent: (context, updateGlobalContext) => (
          <CarsLink
            dealer={dealer}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.REQUEST_FOR_CONSULTATION,
        isActive: enableFormRequestAConsultation,
        renderContent: (context, updateGlobalContext) => (
          <ConsultationAppointment
            {...commonModalProps}
            stateConfig={getStateConfig(
              MODAL_TYPES.REQUEST_FOR_CONSULTATION.name,
              isOsbUser,
              isSalesPartner,
              isServicePartner
            )}
            leadLocation={'Beratungsformular HWS'}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.REQUEST_FOR_CONSULTATION_FK,
        isActive: enableFormRequestAConsultation,
        renderContent: (context, updateGlobalContext) => (
          <ConsultationAppointment
            {...commonModalProps}
            stateConfig={getStateConfig(
              MODAL_TYPES.REQUEST_FOR_CONSULTATION_FK.name,
              isOsbUser,
              isSalesPartner,
              isServicePartner
            )}
            leadLocation={'Individuelle Beratung'}
            consultationAppointmentTab={this.state.consultationAppointmentTab}
            updateTabIndex={this.updateTabIndex}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.REQUEST_FOR_CONSULTATION_WS,
        isActive: enableFormOnlineServiceRequest,
        renderContent: (context, updateGlobalContext) => (
          <ConsultationAppointment
            {...commonModalProps}
            stateConfig={getStateConfig(
              MODAL_TYPES.REQUEST_FOR_CONSULTATION_WS.name,
              isOsbUser,
              isSalesPartner,
              isServicePartner
            )}
            leadLocation={'Individuelle Beratung'}
            consultationAppointmentTab={this.state.consultationAppointmentTab}
            updateTabIndex={this.updateTabIndex}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.REQUEST_FOR_CONSULTATION_OSBFB,
        isActive: enableFormOnlineServiceRequest,
        renderContent: (context, updateGlobalContext) => (
          <ConsultationAppointment
            {...commonModalProps}
            stateConfig={getStateConfig(
              MODAL_TYPES.REQUEST_FOR_CONSULTATION_OSBFB.name,
              isOsbUser,
              isSalesPartner,
              isServicePartner
            )}
            leadLocation={'Individuelle Beratung'}
            consultationAppointmentTab={this.state.consultationAppointmentTab}
            updateTabIndex={this.updateTabIndex}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.CAR_CONFIGURATOR,
        isActive: enableFormCarConfigurator,
        renderContent: (context, updateGlobalContext) => (
          <ConsultationAppointment
            {...commonModalProps}
            isConfigurator={true}
            stateConfig={getStateConfig(
              MODAL_TYPES.CAR_CONFIGURATOR.name,
              isOsbUser,
              isSalesPartner,
              isServicePartner
            )}
            leadLocation={'Beratungsformular HWS / Konfigurationslayer'}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.ONLINE_SERVICE_BOOKING,
        isActive: enableFormOnlineServiceBooking,
        renderContent: (context, updateGlobalContext) => (
          <ConsultationAppointment
            {...commonModalProps}
            stateConfig={getStateConfig(
              MODAL_TYPES.ONLINE_SERVICE_BOOKING.name,
              isOsbUser,
              isSalesPartner,
              isServicePartner
            )}
            leadLocation={'----'}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.SERVICE_BOOKING_FALLBACK,
        isActive: enableFormOnlineServiceRequest,
        renderContent: (context, updateGlobalContext) => (
          <ConsultationAppointment
            {...commonModalProps}
            stateConfig={getStateConfig(
              MODAL_TYPES.SERVICE_BOOKING_FALLBACK.name,
              isOsbUser,
              isSalesPartner,
              isServicePartner
            )}
            leadLocation={'----'}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.SERVICE_REQUEST,
        isActive: enableFormOnlineServiceRequest,
        renderContent: (context, updateGlobalContext) => (
          <ConsultationAppointment
            {...commonModalProps}
            stateConfig={getStateConfig(
              MODAL_TYPES.SERVICE_REQUEST.name,
              isOsbUser,
              isSalesPartner,
              isServicePartner
            )}
            leadLocation={'----'}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.SERVICE_CONTACT,
        isActive: enableFormOnlineServiceRequest && enableFormOnlineServiceBooking,
        renderContent: (context, updateGlobalContext) => (
          <ConsultationAppointment
            {...commonModalProps}
            stateConfig={getStateConfig(
              MODAL_TYPES.SERVICE_CONTACT.name,
              isOsbUser,
              isSalesPartner,
              isServicePartner
            )}
            leadLocation={'----'}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.USED_CAR_EVALUATION,
        isActive: true,
        renderContent: (context, updateGlobalContext) => (
          <UsedCarEvaluation
            openLink={() => {
              if (flyout) flyout();
              window.location.href = '/gebrauchtwagenbewertung/';
            }}
            leadtracking={leadtracking}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
      {
        modalType: MODAL_TYPES.USED_CAR_EVALUATION_DISABLED,
        isActive: true,
        renderContent: (context, updateGlobalContext) => (
          <ConsultationAppointment
            {...commonModalProps}
            stateConfig={getStateConfig(
              MODAL_TYPES.USED_CAR_EVALUATION_DISABLED.name,
              isOsbUser,
              isSalesPartner,
              isServicePartner
            )}
            leadLocation={'----'}
            globalContext={context}
            updateGlobalContext={updateGlobalContext}
          />
        ),
      },
    ];

    const content = contents.find((form) => form.modalType.name === this.modalTypeName);
    const redesignedModalTypes = formTypes.map((ft) => ft.name);
    const showRedesignedLeadForms = urlParams.modaltype
      ? redesignedModalTypes.includes(urlParams.modaltype)
      : redesignedModalTypes.includes(this.modalTypeName);

    if (!content) {
      return null;
    }

    if (!content.isActive) {
      return (
        <Container className={styles.containerBackground}>
          <DeactivatedServiceMessage
            dealer={dealer}
            showRedesignedLeadForms={showRedesignedLeadForms}
          />
        </Container>
      );
    }

    return (
      <GlobalContext.Consumer>
        {({ globalContext, updateGlobalContext }) => (
          <Fragment>
            {content.label && (
              <Container>
                <Row className={styles.marginS}>
                  <Col>
                    <h2>{content.label}</h2>
                  </Col>
                </Row>
              </Container>
            )}
            {content.renderContent(globalContext, updateGlobalContext)}
          </Fragment>
        )}
      </GlobalContext.Consumer>
    );
  }
}

export default withRouter(ContactForm);
